import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { faWarning } from "@fortawesome/free-solid-svg-icons";

import { Button } from "./Button";
import { Flex } from "./Flex";
import { FontSize, Text } from "./Text";
import { Modal } from "./modal/Modal";
import { permissionSliceSelectors } from "../redux/permission/permission.selector";
import { useAppSelector } from "../redux/store";
import { Spacing } from "../theme/Spacing";
import { Info } from "./Info";
import { AtiraCheckBoxGroup } from "./AtiraCheckBoxGroup";
import type { GetProp } from "../utils/local-antd";
import { Checkbox, Tooltip } from "../utils/local-antd";
import { Form } from "../model/form/Form";
import { Entry } from "../model/entry/Entry";
import { BaseTask } from "../model/task/Task";
import { User } from "../model/user/User";
import { TaskKind } from "../model/task/shared/TasksKind.enum";
import { EntryKind } from "../model/entry/types/EntryKind.enum";
import { PermissionType } from "../model/permissions/types/PermissionType";
import { TestIDs } from "../tests/types/testIds";

const getCurrentRelatedPermissionsStateKind = (
  member: User,
  entity: Form | Entry | BaseTask | null,
) => {
  const permissions = member?.permissions?.permissions;
  let kind: "task" | "entry" | "form" | null = null;

  if (Object.keys(TaskKind).includes((entity as any)?.kind)) {
    const task = entity as BaseTask;
    const taskKind = task.kind;
    kind = "task";
    if (
      permissions?.[`${taskKind.toUpperCase()}_TASK` as PermissionType].READ
    ) {
      return null;
    }
  } else if ("formConfig" in entity! || "orphaned" in entity!) {
    kind = "form";
    if (permissions?.FORM.READ) {
      return null;
    }
  } else if (
    "fields" in entity! ||
    Object.keys(EntryKind).includes((entity as any)?.kind)
  ) {
    kind = "entry";
    if (permissions?.ENTRY.READ) {
      return null;
    }
  }
  return kind;
};

const Container = styled(Flex)`
  flex-direction: column;
  width: 100%;
  gap: ${Spacing.m};
`;

const StyledAtiraCheckBoxGroup = styled(AtiraCheckBoxGroup)`
  flex-direction: column;
  gap: ${Spacing.xs};
  width: 100%;

  .ant-checkbox + span {
    width: 100% !important;
    display: flex;
    align-items: center;
  }

  .ant-checkbox-wrapper {
    padding-bottom: ${Spacing.s};
    width: 100% !important;
  }
`;

const MemberName = styled(Text)<{ disabled: boolean }>`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  color: ${({ theme, disabled }) =>
    theme[disabled ? "darkerSub" : "textColor"]};
`;

const WarningInfoWrapper = styled(Flex)`
  & > div {
    gap: ${Spacing.s};
    padding: ${Spacing.xs} ${Spacing.s};
  }

  p,
  svg {
    font-size: ${FontSize.xs};
  }
`;

const MemberContainer = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  padding-bottom: ${Spacing.s};
  column-gap: ${Spacing.m};

  &:not(:last-child) {
    border-bottom: ${({ theme }) => `1px solid ${theme.darkSub}`}aa;
  }

  &:not(:first-child) {
    padding-top: ${Spacing.s};
  }

  .ant-checkbox-wrapper {
    width: fit-content;
  }
`;

type Props = {
  isOpen: boolean;
  onClose: VoidFunction;
  onConfirm: (memberIds: string[]) => Promise<void>;
  entity: Form | Entry | BaseTask | null;
};

export const EntityAssignModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onConfirm,
  entity,
}) => {
  const members = useAppSelector(permissionSliceSelectors.selectMembers);
  const [loading, setLoading] = useState(false);
  const [selectedMembersIds, setSelectedMembersIds] = useState<string[]>([]);
  const { t } = useTranslation(undefined, { keyPrefix: "common.assign.modal" });

  const displayCurrentRelatedPermissionsState = (
    member: User,
  ): React.ReactNode | null => {
    const kind = getCurrentRelatedPermissionsStateKind(member, entity);
    if (!kind) return null;

    return (
      <Tooltip title={t("permissions.warning")}>
        <WarningInfoWrapper>
          <Info
            info={t(`permissions.${kind}`)}
            color="warning"
            icon={faWarning}
          />
        </WarningInfoWrapper>
      </Tooltip>
    );
  };

  const onCheckBoxChange: GetProp<typeof Checkbox.Group, "onChange"> = (
    checkedValues,
  ) => {
    setSelectedMembersIds(checkedValues as string[]);
  };

  const localOnConfirm = async () => {
    setLoading(true);
    await onConfirm(selectedMembersIds);
    setLoading(false);
    onClose();
  };

  useEffect(() => {
    setSelectedMembersIds(
      members
        .filter((m) => entity?.assigneeIds?.includes(m._id))
        .map((m) => m._id),
    );
  }, [members, entity]);

  if (!entity) {
    return null;
  }

  return (
    <Modal
      key={entity._id}
      open={isOpen}
      onClose={onClose}
      content={{ height: "30rem", maxHeight: "35rem", overflow: "hidden" }}
      title={t("title")}
      destroyOnHidden
      zIndex={10000}
    >
      <Container>
        <Info info={t("info")} />
        <Flex flexDirection="column" gap="s">
          <Text color="darkerSub">{t("description")}</Text>

          <StyledAtiraCheckBoxGroup
            options={[]}
            onChange={onCheckBoxChange}
            value={selectedMembersIds}
          >
            {members.map((member) => {
              const disabled = Boolean(
                getCurrentRelatedPermissionsStateKind(member, entity),
              );

              return (
                <MemberContainer key={member._id}>
                  <Checkbox
                    key={member._id}
                    value={member._id}
                    data-testid={TestIDs.permissions.entityAssignModal.selectMember(
                      member._id,
                    )}
                  >
                    <MemberName disabled={disabled}>{member.name}</MemberName>
                  </Checkbox>

                  {displayCurrentRelatedPermissionsState(member)}
                </MemberContainer>
              );
            })}
          </StyledAtiraCheckBoxGroup>
        </Flex>

        <Button
          loading={loading}
          onClick={localOnConfirm}
          margin="none"
          hover
          dataTestId={TestIDs.permissions.entityAssignModal.button}
        >
          {t("button")}
        </Button>
      </Container>
    </Modal>
  );
};
